import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import { toast } from "sonner";

export default function QuotePayment() {
  const { token } = useParams();
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    api
      .get(`/quotes/public/${token}`)
      .then((r) => setQuote(r.data))
      .catch(() => setError("This quote link is invalid or has expired."))
      .finally(() => setLoading(false));
  }, [token]);

  const payWithStripe = async () => {
    if (!quote) return;
    setPaying(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        quote_id: quote.id,
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error("Could not start payment. Please try again.");
      setPaying(false);
    }
  };

  const venmoLink =
    quote?.venmo_username && quote?.final_price
      ? `https://venmo.com/u/${encodeURIComponent(quote.venmo_username)}?txn=pay&amount=${quote.final_price}&note=${encodeURIComponent(
          `Quote ${quote.id.slice(0, 8)} - ${quote.business_name}`
        )}`
      : null;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F7F6F2]">
        <p className="overline">Loading…</p>
      </div>
    );
  }

  if (error || !quote) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-[#F7F6F2] px-6">
        <h1 className="font-serif-heading text-4xl mb-4">Quote not found</h1>
        <p className="text-[#5C5C5C] mb-6">{error}</p>
        <Link to="/" className="btn-primary">Return home</Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F7F6F2] py-16" data-testid="quote-payment-page">
      <div className="container-x max-w-3xl">
        <Link to="/" className="overline text-[#98604A] mb-6 inline-block">← {quote.business_name}</Link>

        <div className="bg-white border border-[#DCD9CE] p-8 md:p-12">
          <p className="overline mb-4 text-[#3F5746]">
            {quote.status === "paid" ? "Paid · Thank You" : "Quote Ready"}
          </p>
          <h1 className="font-serif-heading text-4xl md:text-5xl mb-2 text-[#232323]">
            For {quote.name}
          </h1>
          <p className="text-[#5C5C5C] mb-8">
            {quote.cemetery_name}
          </p>

          <div className="border-t border-b border-[#DCD9CE] py-6 mb-8 space-y-3">
            <div className="flex justify-between text-sm">
              <span className="text-[#5C5C5C]">Stone size</span>
              <span className="text-[#232323]">{quote.stone_size_label}</span>
            </div>
            {quote.addon_labels.length > 0 && (
              <div className="flex justify-between text-sm">
                <span className="text-[#5C5C5C]">Add-ons</span>
                <span className="text-[#232323] text-right">{quote.addon_labels.join(", ")}</span>
              </div>
            )}

            <div className="flex justify-between text-sm">
              <span className="text-[#5C5C5C]">Preliminary estimate</span>
              <span className="text-[#232323] line-through">${quote.estimated_price.toFixed(2)}</span>
            </div>
            <div className="flex justify-between items-end pt-3">
              <span className="overline text-[#98604A]">Total due</span>
              <span className="font-serif-heading text-5xl text-[#232323]" data-testid="quote-total">
                ${quote.final_price ? quote.final_price.toFixed(2) : quote.estimated_price.toFixed(2)}
              </span>
            </div>
          </div>

          {quote.status === "paid" ? (
            <div className="bg-[#EBE8E0] p-6 text-center" data-testid="quote-paid-banner">
              <p className="font-serif-heading text-2xl mb-2">Payment received</p>
              <p className="text-sm text-[#5C5C5C]">
                We'll be in touch shortly with scheduling details.
              </p>
            </div>
          ) : !quote.payable ? (
            <div className="bg-[#EBE8E0] p-6">
              <p className="font-serif-heading text-2xl mb-2">Quote pending finalization</p>
              <p className="text-sm text-[#5C5C5C]">
                We've received your request and will email a firm quote shortly. This
                page will become payable once the final price is set.
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              <p className="overline text-[#98604A] mb-2">Choose payment method</p>

              <button
                type="button"
                onClick={payWithStripe}
                disabled={paying}
                className="btn-primary w-full justify-center py-5 text-sm"
                data-testid="pay-stripe-button"
              >
                {paying ? "Redirecting…" : "Pay with Card or e-Check"}
              </button>
              <p className="text-xs text-[#5C5C5C] text-center -mt-2">
                Powered by Stripe · credit/debit cards and US bank ACH accepted
              </p>

              {venmoLink && (
                <>
                  <div className="flex items-center gap-4 my-6">
                    <div className="flex-1 h-px bg-[#DCD9CE]" />
                    <span className="overline text-[#5C5C5C]">or</span>
                    <div className="flex-1 h-px bg-[#DCD9CE]" />
                  </div>
                  <a
                    href={venmoLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-secondary w-full justify-center py-5 text-sm"
                    data-testid="pay-venmo-link"
                  >
                    Pay with Venmo · @{quote.venmo_username}
                  </a>
                  <p className="text-xs text-[#5C5C5C] text-center -mt-2">
                    Venmo payments are confirmed manually. Please include quote #{quote.id.slice(0, 8)} in your note.
                  </p>
                </>
              )}
            </div>
          )}
        </div>

        <p className="text-center text-xs text-[#5C5C5C] mt-8">
          Questions? Reply to the email this link came from and we'll respond promptly.
        </p>
      </div>
    </div>
  );
}
